export function toText(value, fallback = '') {
  const normalized = String(value ?? '').trim();
  return normalized || fallback;
}

export function displayValue(value) {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map(displayValue).filter(Boolean).join('、');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export function createElement(tag, options = {}, children = []) {
  const node = document.createElement(tag);
  if (options.className) node.className = options.className;
  if (options.text != null) node.textContent = String(options.text);
  if (options.id) node.id = options.id;
  if (options.attrs) {
    for (const [name, value] of Object.entries(options.attrs)) {
      if (value == null || value === false) continue;
      node.setAttribute(name, value === true ? '' : String(value));
    }
  }
  if (options.dataset) {
    for (const [name, value] of Object.entries(options.dataset)) {
      if (value != null) node.dataset[name] = String(value);
    }
  }
  appendChildren(node, children);
  return node;
}

export function appendChildren(parent, children) {
  const rows = Array.isArray(children) ? children.flat(Infinity) : [children];
  for (const child of rows) {
    if (child == null || child === false) continue;
    parent.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return parent;
}

export function replaceChildren(parent, children = []) {
  parent.replaceChildren();
  appendChildren(parent, children);
  return parent;
}

export function svgIcon(symbolId, className = '') {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  if (className) svg.setAttribute('class', className);
  svg.setAttribute('aria-hidden', 'true');
  const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
  const safeId = /^[a-z0-9-]+$/i.test(symbolId) ? symbolId : 'i-file';
  use.setAttribute('href', `#${safeId}`);
  svg.append(use);
  return svg;
}

export function actionButton({ label, icon, className = 'icon-btn', disabled = false, dataset = {} }) {
  const button = createElement('button', {
    className,
    attrs: { type: 'button', 'aria-label': label, title: label },
    dataset
  });
  button.disabled = disabled;
  if (icon) button.append(svgIcon(icon));
  else button.textContent = label;
  return button;
}

export function labeledField({ label, value, multiline = false, className = '', onChange }) {
  const control = createElement(multiline ? 'textarea' : 'input');
  if (!multiline) control.type = 'text';
  control.value = toText(value);
  control.addEventListener('change', () => onChange?.(control.value));
  return createElement('label', { className: `field ${className}`.trim() }, [
    createElement('span', { text: label }),
    control
  ]);
}

export function safeImageUrl(value) {
  const source = toText(value);
  if (!source) return '';
  if (/^data:image\/(png|jpe?g|gif|webp);/i.test(source)) return source;
  if (/^blob:/i.test(source)) return source;
  try {
    const parsed = new URL(source, window.location.href);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : '';
  } catch {
    return '';
  }
}

export function setHidden(node, hidden) {
  node.hidden = Boolean(hidden);
  node.classList.toggle('hidden', Boolean(hidden));
  node.setAttribute('aria-hidden', hidden ? 'true' : 'false');
}

export function requiredElement(id) {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing required UI element #${id}`);
  return node;
}

export function listen(target, type, listener, options) {
  target.addEventListener(type, listener, options);
  return () => target.removeEventListener(type, listener, options);
}
